import { useMutation } from '#client/hooks/useMutation';
import { api } from '#client/lib/api';
import { useToast } from '#client/providers/ToastProvider';
import { Modal } from './Modal';

interface ConfirmDeleteModalProps {
  /** The record to delete, or null when the modal is closed. */
  target: { id: number; label: string } | null;
  /** API path without the id, e.g. `/panels`. */
  path: string;
  onClose: () => void;
  onDeleted: () => void;
  /** Noun shown in the prompt, e.g. "panel". */
  noun: string;
}

/**
 * Every delete goes through a confirmation with an error surface, so a
 * misclick cannot silently destroy a record.
 */
export function ConfirmDeleteModal({
  target,
  path,
  onClose,
  onDeleted,
  noun,
}: ConfirmDeleteModalProps) {
  const toast = useToast();
  const deletion = useMutation(`Could not delete this ${noun}`);
  const deleting = deletion.isPending;

  async function handleDelete() {
    if (!target) return;
    const outcome = await deletion.run(() => api.delete(`${path}/${target.id}`));
    if (!outcome.ok) {
      // e.g. a lab test still referenced by reports comes back as a 400.
      toast.error(outcome.message);
      return;
    }
    toast.success(`Deleted ${target.label}`);
    onDeleted();
    onClose();
  }

  return (
    <Modal
      open={target !== null}
      title={`Delete ${noun}`}
      onClose={onClose}
      primaryAction={{
        label: deleting ? 'Deleting…' : 'Delete',
        onAction: () => void handleDelete(),
        loading: deleting,
        destructive: true,
      }}
      secondaryAction={{ label: 'Cancel', onAction: onClose }}
    >
      <p>
        Delete <strong>{target?.label}</strong>? This cannot be undone.
      </p>
    </Modal>
  );
}
